import { X } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from '../../../lib/i18n';
import type { Home3ReadState } from '../../../lib/home3-feed';
import { Card, IconButton } from '../ui';
import { Home3Summary } from './Home3Summary';
import styles from './Home3BriefPopover.module.css';

/**
 * The brief over the canvas (Figma 2017:84701) — what the Brief entry point opens, and what the canvas opens
 * by itself once a page load (`Home3Body`).
 *
 * It is the board's own brief card, `Home3Summary`, drawn in its `popover` presentation inside a DS card with
 * a close button: the same four states (the day's numbers, the paused routine, the routine never set up, the
 * first morning's Space), the same "Get the full briefing" into the chat and the same read-along. So the canvas
 * and the board cannot say two things about one morning.
 *
 * **Not a `Modal`.** The frame draws it over the live map with no scrim and no shadow, as a card the reader
 * can leave open while the map runs behind it — a dialog that does not take the page. So it is `role="dialog"`
 * without `aria-modal`, it does not trap focus, and Escape, the X or a press anywhere outside it closes it. An action that takes the reader
 * somewhere else closes it too (`onLeave`), so it is not still standing there when they come back.
 */
export function Home3BriefPopover({
  readState,
  closed,
  moving,
  className,
  onClose,
}: {
  readState: Home3ReadState;
  closed: number;
  moving: number;
  /** Where the canvas places it — the host's own class. */
  className?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Escape from INSIDE the popover only. The read-along opens a real `Modal`, portalled out of this subtree
  // but still a React child of it, so its own Escape bubbles here — and closing the popover then would
  // unmount the listen modal mid-sentence.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && event.currentTarget.contains(event.target as Node)) onClose();
  };

  // A press outside closes it. "Inside" is the React tree, not the DOM: the read-along's `Modal` is portalled out
  // of this subtree, and a press in it must not close the popover under it. React delivers the capture handler
  // below through portals before the press reaches the document, so it marks the press as ours first. The Brief
  // entry point is the popover's own toggle, and closes it by itself.
  const inside = useRef(false);
  const insideKey = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onPress = (event: PointerEvent) => {
      if (inside.current) {
        inside.current = false;
        return;
      }
      if (event.target instanceof Element && event.target.closest('[data-brief-toggle]')) return;
      close.current();
    };
    // Escape from anywhere on the page closes it too — opened from the Brief entry point, focus stays on that
    // button, outside the popover. Not an Escape that started inside it (the read-along's own, for one), which
    // the handler above already answers.
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (insideKey.current) {
        insideKey.current = false;
        return;
      }
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('pointerdown', onPress);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPress);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div
      className={className}
      role="dialog"
      aria-label={t('home.map.brief')}
      onKeyDown={onKeyDown}
      onPointerDownCapture={() => {
        inside.current = true;
      }}
      onKeyDownCapture={() => {
        insideKey.current = true;
      }}
    >
      <Card variant="sunken" disablePadding>
        <div className={styles.body}>
          <div className={styles.close}>
            <IconButton size="tiny" aria-label={t('common.close')} onClick={onClose}>
              <X size={20} aria-hidden />
            </IconButton>
          </div>
          <Home3Summary readState={readState} closed={closed} moving={moving} popover onLeave={onClose} />
        </div>
      </Card>
    </div>
  );
}
